import { useCallback, useState } from 'react';

import { useDeleteJobLevels } from '../api/use-delete-job-levels';
import { useDeleteJobRoles } from '../api/use-delete-job-roles';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useUnarchiveJobLevels } from '../api/use-unarchive-job-levels';
import { useUnarchiveJobRoles } from '../api/use-unarchive-job-roles';
import { useUrlNavigationActions } from '../url-navigation/use-url-navigation-actions';

type UseArchivedItemActionsProps = {
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: 'job-level' | 'job-role';
    readonly onDeleteComplete?: () => void;
    readonly onRestoreComplete?: () => void;
};

type UseArchivedItemActionsResult = {
    readonly handleDelete: () => Promise<void>;
    readonly handleRestore: () => Promise<void>;
    readonly isDeleteDialogOpen: boolean;
    readonly setIsDeleteDialogOpen: (open: boolean) => void;
};

export const useArchivedItemActions = ({
    architectureId,
    item,
    itemType,
    onDeleteComplete,
    onRestoreComplete,
}: UseArchivedItemActionsProps): UseArchivedItemActionsResult => {
    const { mutateAsync: unarchiveRoles } = useUnarchiveJobRoles();
    const { mutateAsync: unarchiveLevels } = useUnarchiveJobLevels();
    const { mutateAsync: deleteRoles } = useDeleteJobRoles();
    const { mutateAsync: deleteLevels } = useDeleteJobLevels();
    const { closeSidepanel } = useUrlNavigationActions();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const handleRestore = useCallback(async (): Promise<void> => {
        await (itemType === 'job-role'
            ? unarchiveRoles({ jobArchitectureId: architectureId, jobRoleIds: [item.id] })
            : unarchiveLevels({ jobArchitectureId: architectureId, jobLevelIds: [item.id] }));

        closeSidepanel(['job-profile']);
        onRestoreComplete?.();
    }, [architectureId, closeSidepanel, item.id, itemType, onRestoreComplete, unarchiveLevels, unarchiveRoles]);

    const handleDelete = useCallback(async (): Promise<void> => {
        await (item.type === 'job-role' ? deleteRoles({ jobRoles: [item] }) : deleteLevels({ jobLevels: [item] }));

        setIsDeleteDialogOpen(false);
        closeSidepanel(['job-profile']);
        onDeleteComplete?.();
    }, [closeSidepanel, deleteLevels, deleteRoles, item, onDeleteComplete]);

    return { handleDelete, handleRestore, isDeleteDialogOpen, setIsDeleteDialogOpen };
};
